'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Mic, Square, Play, Pause, Loader2, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { WaveformVisualizer } from '@/components/voice/WaveformVisualizer';
import { trpc } from '@/lib/trpc/client';

interface VoiceNoteWidgetProps {
  onTranscriptionComplete?: (transcript: string, summary: string) => void;
  onDiscard?: () => void;
}

const VoiceNoteWidget: React.FC<VoiceNoteWidgetProps> = ({ onTranscriptionComplete, onDiscard }) => {
  const { t } = useTranslation();
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  const {
    isRecording,
    isPaused,
    recordingTime,
    audioURL,
    audioBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearRecording,
    waveformData,
  } = useAudioRecorder();

  // tRPC mutations
  const transcribeMutation = trpc.voice.transcribe.useMutation({
    onSuccess: (data) => {
      if (data.transcription) {
        setTranscript(data.transcription);
        // Automatically summarize after transcription
        summarizeMutation.mutate({ text: data.transcription });
      }
    },
    onError: (error) => {
      console.error('Transcription error:', error);
      setError(t('voiceWidget.errors.transcriptionFailed', 'Failed to transcribe audio. Please try again.'));
    },
  });

  const summarizeMutation = trpc.voice.summarize.useMutation({
    onSuccess: (data) => {
      if (data.summary) {
        setSummary(data.summary);
      }
    },
    onError: (error) => {
      console.error('Summarization error:', error);
      setError(t('voiceWidget.errors.summarizationFailed', 'Failed to summarize. Please try again.'));
    },
  });

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleReset = useCallback(() => {
    clearRecording();
    setTranscript('');
    setSummary('');
    setError(null);
    setHasProcessed(false);
  }, [clearRecording]);

  const blobToBase64 = useCallback((blob: Blob) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        const base64 = result.split(',')[1];
        if (base64) {
          resolve(base64);
        } else {
          reject(new Error('Unable to convert blob to base64'));
        }
      };
      reader.onerror = () => reject(reader.error ?? new Error('Unable to read audio data'));
      reader.readAsDataURL(blob);
    });
  }, []);

  const handleTranscribe = useCallback(async (url?: string) => {
    const audioToTranscribe = url || audioURL;
    if (!audioToTranscribe && !audioBlob) {
      return;
    }

    setError(null);

    try {
      const blob =
        audioBlob ??
        await (async () => {
          if (!audioToTranscribe) {
            throw new Error('Missing audio URL');
          }
          const response = await fetch(audioToTranscribe);
          if (!response.ok) {
            throw new Error('Failed to fetch audio');
          }
          return response.blob();
        })();

      const base64 = await blobToBase64(blob);

      transcribeMutation.mutate({
        audioBuffer: base64,
        fileName: 'recording.webm',
        mimeType: blob.type || 'audio/webm',
      });
    } catch (error) {
      console.error('Transcription error:', error);
      setError(t('voiceWidget.errors.transcriptionFailed', 'Failed to transcribe audio. Please try again.'));
    }
  }, [audioURL, audioBlob, transcribeMutation, t, blobToBase64]);

  const handleApplyToForm = useCallback(() => {
    if (transcript || summary) {
      // Combine transcript and summary for the message field
      const fullMessage = summary
        ? `${summary}\n`
        : transcript;

      onTranscriptionComplete?.(transcript, fullMessage);

      // Show completion status briefly then reset
      setTimeout(() => {
        handleReset();
        onDiscard?.(); // Auto-close the widget
      }, 2000);
    }
  }, [transcript, summary, onTranscriptionComplete, onDiscard, handleReset]);

  // Auto-transcribe when recording stops and we have an audio URL
  useEffect(() => {
    if (audioURL && !isRecording && !hasProcessed) {
      handleTranscribe(audioURL);
      setHasProcessed(true);
    }
  }, [audioURL, isRecording, hasProcessed, handleTranscribe]);

  // Auto-apply to form when summary is ready
  useEffect(() => {
    if (summary && transcript && !summarizeMutation.isPending && !transcribeMutation.isPending) {
      handleApplyToForm();
    }
  }, [summary, transcript, summarizeMutation.isPending, transcribeMutation.isPending, handleApplyToForm]);

  // const handleDiscard = () => {
  //   handleReset();
  //   onDiscard?.();
  // };

  const MAX_RECORDING_TIME = 180000; // 3 minutes in milliseconds
  const canRecord = recordingTime < MAX_RECORDING_TIME;

  return (
    <div className="bg-primary-50 rounded-xl border border-primary-200 p-6 space-y-4">

      <div className="text-center">
        <p className="text-primary-700 text-sm">
          {t('voiceWidget.subtitle', 'Speak your travel ideas - automatically added to your message')}
        </p>
      </div>

      {/* Recording Status Badge */}
      {(isRecording || audioURL) && (
        <div className="flex items-center justify-center">
          {isRecording && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isPaused
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800 animate-pulse'
            }`}>
              {isPaused ? 'Paused' : 'Recording'} • {formatTime(recordingTime)}
            </span>
          )}
          {audioURL && !isRecording && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Recording Complete • {formatTime(recordingTime)}
            </span>
          )}
        </div>
      )}

      {/* Waveform Visualizer */}
      <div className="bg-white rounded-lg p-2">
        <WaveformVisualizer
          waveformData={waveformData}
          isRecording={isRecording}
          isPaused={isPaused}
          width={400}
          height={80}
        />
      </div>

      {/* Recording Controls */}
      <div className="flex justify-center gap-2">
        {!isRecording && !audioURL ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={!canRecord}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-550 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mic className="h-4 w-4 mr-2" />
            {t('voiceWidget.startRecording', 'Start Recording')}
          </button>
        ) : isRecording ? (
          <>
            <button
              type="button"
              onClick={isPaused ? resumeRecording : pauseRecording}
              className="px-3 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center text-sm"
            >
              {isPaused ? (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  {t('voiceWidget.resume', 'Resume')}
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-1" />
                  {t('voiceWidget.pause', 'Pause')}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={stopRecording}
              className="px-3 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center text-sm"
            >
              <Square className="h-4 w-4 mr-1" />
              {t('voiceWidget.stop', 'Stop')}
            </button>
          </>
        ) : null}

        {audioURL && !isRecording && !transcript && (
          <button
            type="button"
            onClick={handleReset}
            className="px-3 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center text-sm"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            {t('voiceWidget.reRecord', 'Re-record')}
          </button>
        )}
      </div>

      {/* Processing Status */}
      {(transcribeMutation.isPending || summarizeMutation.isPending) && (
        <div className="flex justify-center items-center space-x-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
          <span className="text-sm text-primary-700">
            {transcribeMutation.isPending
              ? t('voiceWidget.transcribing', 'Transcribing your message...')
              : t('voiceWidget.summarizing', 'Creating summary...')}
          </span>
        </div>
      )}

      {/* Completion Status */}
      {summary && transcript && !summarizeMutation.isPending && !transcribeMutation.isPending && (
        <div className="flex items-center justify-center space-x-2 text-green-700">
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">
            {t('voiceWidget.addedToMessage', 'Voice message added to form')}
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default VoiceNoteWidget;